import type {
  InboxFilter,
  InboxGroup,
  InboxImportance,
  ReclassifiableSeverity,
  SimulatedEventKind,
  SimulationAudience,
} from '../contracts/inbox';
import type { MessageImportance } from '../contracts/mail';

/** Sekcje listy Inbox (Figma 02). */
export const INBOX_GROUP_LABELS: Record<InboxGroup, string> = {
  urgent: 'Pilne',
  new: 'Nowe',
  earlier: 'Wcześniej',
};

/** Zakładki listy (domyślnie „Wszystkie” = `open`). */
export const INBOX_FILTER_LABELS: Record<InboxFilter, string> = {
  unread: 'Nieprzeczytane',
  open: 'Wszystkie',
  snoozed: 'Odłożone',
  done: 'Zrobione',
  all: 'Wszystko, także zrobione',
};

function pluralForm(n: number): 0 | 1 | 2 {
  if (n === 1) return 0;
  const last = n % 10;
  const lastTwo = n % 100;
  return last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14) ? 1 : 2;
}

/**
 * Baner nad listą Inboxu: „3 akcje AI czekają na akceptację” (Figma 02). `ai: false`, gdy nie wszystkie czekające
 * akcje zlecił Copilot / agent: „3 akcje czekają na akceptację”.
 */
export function pendingApprovalsBanner(n: number, opts: { ai?: boolean } = {}): string {
  const form = pluralForm(n);
  const noun = (['akcja', 'akcje', 'akcji'] as const)[form];
  const verb = (['czeka', 'czekają', 'czeka'] as const)[form];
  return `${n} ${noun}${opts.ai === false ? '' : ' AI'} ${verb} na akceptację`;
}

/** Baner na mobile (Figma 02 mobile): „3 akcje AI do akceptacji”. */
export function pendingApprovalsShort(n: number, opts: { ai?: boolean } = {}): string {
  const noun = (['akcja', 'akcje', 'akcji'] as const)[pluralForm(n)];
  return `${n} ${noun}${opts.ai === false ? '' : ' AI'} do akceptacji`;
}

/** „2 odłożone” (link pod listą). */
export function snoozedCountLabel(n: number): string {
  return `${n} ${(['odłożona', 'odłożone', 'odłożonych'] as const)[pluralForm(n)]}`;
}

/** Nazwy rekordów w „Powiązania” podglądu (gdy rekord nie ma własnej nazwy). */
export const INBOX_LINK_TYPE_LABELS: Record<string, string> = {
  approval: 'Akceptacja',
  client: 'Klient',
  project: 'Projekt',
  task: 'Zadanie',
  hosted_asset: 'Zasób',
  billing_draft: 'Zestawienie',
  meeting: 'Spotkanie',
  offer: 'Oferta',
  knowledge_item: 'Notatka',
  message_thread: 'Wątek',
  message: 'Wiadomość',
  monitor: 'Monitor',
  incident: 'Incydent',
  integration: 'Integracja',
};

/** Główny przycisk podglądu („Przejdź do akceptacji”, „Przejdź do zasobu”). */
export const INBOX_GO_TO_LABELS: Record<string, string> = {
  approval: 'Przejdź do akceptacji',
  client: 'Przejdź do klienta',
  project: 'Przejdź do projektu',
  task: 'Przejdź do zadania',
  hosted_asset: 'Przejdź do zasobu',
  billing_draft: 'Przejdź do zestawienia',
  meeting: 'Przejdź do spotkania',
  offer: 'Przejdź do oferty',
  knowledge_item: 'Przejdź do notatki',
  message_thread: 'Otwórz wątek',
  incident: 'Przejdź do incydentu',
  monitor: 'Przejdź do zasobu',
  message: 'Otwórz wątek',
  integration: 'Przejdź do integracji',
};

/** Opis skutku ważności w oknie „Zmień ważność” (MVP §14: powiadomienia). */
export const RECLASSIFY_SEVERITY_HINTS: Record<ReclassifiableSeverity, string> = {
  critical: 'Na górze Inboxu i w alarmach na ekranie Dziś. Wysyła powiadomienie na telefon.',
  normal: 'W Inboxie, bez powiadomienia na telefon.',
  fyi: 'Znika z Inboxu. Zostaje w historii klienta.',
};

/**
 * Istotność odebranej poczty (DECISIONS 2026-10-17 [P] „Każda poczta w Inboxie, push tylko o istotnych”): wiersz
 * Inboxu, wątek, „Zmień ważność” maila. „Mniej ważne” = ważność „Do wiadomości” odebranego maila.
 */
export const MAIL_IMPORTANCE_LABELS: Record<MessageImportance, string> = {
  critical: 'Pilne',
  important: 'Istotne',
  less: 'Mniej ważne',
};

/** Filtr Inboxu „Istotne / Wszystkie”. */
export const INBOX_IMPORTANCE_LABELS: Record<InboxImportance, string> = {
  all: 'Wszystkie',
  important: 'Istotne',
};

/** „Zmień ważność” odebranego maila: te same wartości, nazwy istotności. */
export const MAIL_RECLASSIFY_LABELS: Record<ReclassifiableSeverity, string> = {
  critical: 'Pilne',
  normal: 'Istotne',
  fyi: 'Mniej ważne',
};

export const RECLASSIFY_MAIL_HINTS: Record<ReclassifiableSeverity, string> = {
  critical: 'Na górze Inboxu i w alarmach na ekranie Dziś. Wysyła powiadomienie na telefon.',
  normal: 'W Inboxie jako nieprzeczytana. Powiadomienie „Nowa poczta” w trybie „Istotne”.',
  fyi: 'Zostaje w Inboxie jako „Mniej ważne” — przeczytana, bez plakietki i bez powiadomienia w trybie „Istotne”.',
};

/** Rodzaje zdarzeń w oknie „Symuluj zdarzenie”. */
export const SIMULATED_EVENT_KIND_LABELS: Record<SimulatedEventKind, string> = {
  message: 'Wiadomość od klienta',
  outage: 'Awaria strony',
  agent_finished: 'Agent skończył pracę',
};

export const SIMULATION_AUDIENCE_LABELS: Record<SimulationAudience, string> = {
  me: 'Tylko do mnie',
  record: 'Do wszystkich z dostępem do klienta',
};
